import * as THREE from 'three';
import gsap from 'gsap';

import Experience from '../Experience';
import { beatsPerMeasure, songNotes } from './Songs/song1/song1';
import ScoreCalculator from './ScoreCalculator';
import Renderer from '../Renderer';
import Car from '../World/Car';

export default class GameView {
    constructor(key, musicDelay, noteInterval) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.key = key;
        this.musicDelay = musicDelay;
        this.noteInterval = noteInterval;
        this.renderer = new Renderer();
        this.note = {};
        this.materials = {};

        this.zStartPoint = 20;
        this.zEndPoint = 0.5;
        this.yStartPoint = 0.757;
        this.yEndPoint = 0.757;
        this.frameCounter = 0;
        this.frameCounter2 = 0;
        this.xPos = [0.105, 0.025, -0.05, -0.123];
        this.noteCounter = 0;

        this.spheres = [];
        this.circles = [];
        this.cylinders = [];
        this.beatLines = [];

        this.t = 0;
        this.measures = [0];

        this.fps = 24;
        this.frameToStartGame = 48;
        this.frameToShowNotes = 260;
        this.frameWhichWeShouldAddNote = this.noteInterval / 1000 * this.fps;

        this.car = new Car();
        this.figures = {};

        this.car.model.traverse(m => {
            if(m.isMesh) {
                m.emissiveIntensity = 0.4;
                if(m.name === 'FigureA') this.figures['0'] = m;
                if(m.name === 'FigureS') this.figures['1'] = m;
                if(m.name === 'FigureD') this.figures['2'] = m;
                if(m.name === 'FigureF') this.figures['3'] = m;
            }
        })
    }

    setup() {
        this.setNotes();
        this.setupBaseLineMaterialsAndGeometry();
    }

    setNotes() {
        this.note.vel = .0038125;

        this.note.yVel = this.note.vel * (this.yEndPoint - this.yStartPoint);
        this.note.zVel = this.note.vel * (this.zEndPoint - this.zStartPoint);

        this.note.radius = 0.01;

        this.note.geometry = new THREE.CylinderGeometry(0.02, 0.02, 0.01, 32);
        this.note.colors = [];
        this.note.colors[0] = 0x4C7048; // Green
        this.note.colors[1] = 0xDA3A3C; // Red
        this.note.colors[2] = 0xffeb3b; // Yellow
        this.note.colors[3] = 0x3f51b5; // Blue
        this.note.colors[4] = 0xea0000; // Red - selected
        this.note.colors[5] = 0x22ff00; // Green - selected
        this.redColor = new THREE.Color(1, 0 , 0);
        this.greenColor = new THREE.Color(0, 1 , 0);
        this.blackColor = new THREE.Color(0, 0 , 0);

        this.note.materials = [];
        this.note.colors.forEach((color, idx) => {
            this.note.materials[idx] = new THREE.MeshBasicMaterial({
                color: this.note.colors[idx],
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0
            });
        });
    }

    lightUpCirclesWhenPressButton() {

        for (let i = 0; i < 4; i++) {
                if (this.key.isDownVisually(this.key.pos[i + 1]) && this.gameCalculator?.rightShots[i + 1]) {
                    this.figures[i].material.emissive = this.greenColor;
                    setTimeout(() => this.figures[i].material.emissive = this.blackColor, 150);
                } else if(this.key.isDownVisually(this.key.pos[i + 1]) && !this.gameCalculator?.rightShots[i + 1]) {
                    this.figures[i].material.emissive = this.redColor;
                    setTimeout(() => this.figures[i].material.emissive = this.blackColor, 150);
                } else {
                    // this.figures[i].material.emissive = this.blackColor;
                }
            }
    }

    createNotes(noteInterval) {
        let noteMaterial;

        this.gameCalculator = new ScoreCalculator(noteInterval, this.musicDelay, this.key, this.frameWhichWeShouldAddNote);

        songNotes.forEach((songNote, idx) => {
            noteMaterial = this.note.materials[songNote.pos - 1];

            this.spheres[idx] = new THREE.Mesh(this.note.geometry, noteMaterial);
            this.spheres[idx].layers.enable( this.renderer.BLOOM_SCENE )
            this.spheres[idx].scale.set(1.0, 1.0, 2.0);
            let time = noteInterval * (((songNote.m - 1) * beatsPerMeasure) + songNote.t);
            let lag = 0;
            lag = 5 * songNote.m;
            time += lag;
        });
    }

    addMovingNotesToScene(index) {
        this.spheres[index].position.set(this.xPos[songNotes[index].pos - 1], (this.yStartPoint), (this.zStartPoint));
        this.scene.add(this.spheres[index]);
    }

    setupBaseLineMaterialsAndGeometry() {
        this.onBeatLineMaterial =
            new THREE.MeshBasicMaterial( { color: 0x999999});
        this.offBeatLineMaterial =
            new THREE.MeshBasicMaterial( { color: 0x3b3b3b});
        this.beatLineGeometry = new THREE.CylinderGeometry(
            .0005, .0005, (this.xPos[0] - this.xPos[3] + 0.03)
        );
    }

    moveAllGuitarElements() {
        if (!this.spheres.length) return;
        this.spheres.forEach(sphere => {
            sphere.position.y += this.note.yVel;
            sphere.position.z += this.note.zVel;
            if (sphere.position.z < this.zEndPoint) {
                this.scene.remove(sphere);
            }
        });
    }

    handleWin() {
        return this.gameCalculator.handleWin();
    }

    startGame() {
        if(this.started) return;
        gsap.to('[data-game-details]', {autoAlpha: 1});
        this.createNotes(this.noteInterval);
        this.started = true;
        this.frameCounter = 0;
    }

    showNotes() {
        gsap.to(this.note.materials, {opacity: 1, duration: 0.7});
    }

    update() {
        this.frameCounter++;
        this.frameCounter2++;

        if(this.frameCounter === this.frameToStartGame) this.startGame();
        if(this.frameCounter2 === this.frameToShowNotes) this.showNotes();
        if(
            this.noteCounter < songNotes.length
            && this.started
            && (this.noteCounter % 2 && this.frameCounter === Math.ceil(this.frameWhichWeShouldAddNote)
            || !(this.noteCounter % 2) && this.frameCounter === Math.floor(this.frameWhichWeShouldAddNote)))
        {
                this.addMovingNotesToScene(this.noteCounter);
                this.noteCounter++;
                this.frameCounter = 0;
        }
        if(this.gameCalculator) this.gameCalculator.update();

        this.lightUpCirclesWhenPressButton();
        this.moveAllGuitarElements();
    }
}


